/**
 * NavigatorX Voice Cloning Service
 * 
 * Service for managing voice cloning feature administration:
 * - Voice clone listing and filtering
 * - Status management
 * - Voice synthesis integration (Qwen3-TTS)
 * - Failed processing retry
 * - Voice deletion with cleanup
 * - Analytics and statistics
 * 
 * @author Claude AI for TGE.LA
 * @version 1.0.0
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  collectionGroup
} from 'firebase/firestore';
import { navDb, functions } from '../firebase/client';
import { httpsCallable } from 'firebase/functions';
import { logAdminAction } from './navigatorXAuditService';
import { NAV_USERS_COLLECTION } from './navigatorXService';

// ============================================================================
// CONSTANTS
// ============================================================================

const VOICES_SUBCOLLECTION = 'loved_ones_voices';
const RECORDING_SESSIONS_SUBCOLLECTION = 'voice_recording_sessions';
const CREATED_VOICES_SUBCOLLECTION = 'created_voices';
const MEDIA_CLONED_VOICES_SUBCOLLECTION = 'media_cloned_voices';

/** Voice recording status values (matching Android app) */
export const VOICE_RECORDING_STATUS = {
  PENDING: 'PENDING',
  RECORDING: 'RECORDING',
  PROCESSING: 'PROCESSING',
  READY: 'READY',
  FAILED: 'FAILED'
} as const;

export type VoiceRecordingStatus = typeof VOICE_RECORDING_STATUS[keyof typeof VOICE_RECORDING_STATUS];

/** Voice relationship types (matching Android app) */
export const VOICE_RELATIONSHIPS = [
  'WIFE',
  'HUSBAND',
  'GIRLFRIEND',
  'BOYFRIEND',
  'DAUGHTER',
  'SON',
  'MOTHER',
  'FATHER',
  'FRIEND',
  'COWORKER',
  'OTHER'
] as const;

export type VoiceRelationship = typeof VOICE_RELATIONSHIPS[number];

/** Created voice status values (matching Android app) */
export const CREATED_VOICE_STATUS = {
  DESIGNING: 'DESIGNING',
  GENERATING_PREVIEW: 'GENERATING_PREVIEW',
  PREVIEW_READY: 'PREVIEW_READY',
  REGENERATING: 'REGENERATING',
  FINALIZING: 'FINALIZING',
  READY: 'READY',
  FAILED: 'FAILED'
} as const;

export type CreatedVoiceStatus = typeof CREATED_VOICE_STATUS[keyof typeof CREATED_VOICE_STATUS];

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Voice clone profile
 * Stored at: nav_users/{uid}/loved_ones_voices/{voiceId}
 */
export interface VoiceClone {
  id: string;
  userId: string;
  
  // Profile Info
  name: string;
  relationship: VoiceRelationship | string;
  nickname: string;
  phone?: string;
  email?: string;
  
  // Voice Settings
  wakeWord?: string;
  volume: number;
  pitch: number;
  speed: number;
  isActive: boolean;
  
  // Recording Status
  recordingStatus: VoiceRecordingStatus;
  recordingToken?: string;
  recordingSessionId?: string;
  
  // Payment
  isPaid: boolean;
  purchaseToken?: string;
  
  // Voice Synthesis ID (legacy field name from ElevenLabs era, now used by Qwen3-TTS)
  voiceId?: string;
  elevenLabsVoiceId?: string;
  qualityScore?: number;
  
  // Recording URLs
  rawRecordingUrl?: string;
  processedRecordingUrl?: string;
  
  // Cached phrases for offline use
  cachedPhrases?: Record<string, string>;
  
  // Timestamps
  createdAt: Timestamp | string;
  recordedAt?: Timestamp | string;
  processedAt?: Timestamp | string;
  updatedAt?: Timestamp | string;
  
  // Error tracking
  lastError?: string;
  errorCount?: number;
  lastRetryAt?: Timestamp | string;
  
  // User display name for admin view
  userDisplayName?: string;
  userEmail?: string;
}

/**
 * Voice recording session
 * Stored at: nav_users/{uid}/voice_recording_sessions/{sessionId}
 */
export interface VoiceRecordingSession {
  id: string;
  voiceId: string;
  status: string;
  startedAt: Timestamp | string;
  completedAt?: Timestamp | string;
  recordings?: RecordingEntry[];
  errorMessage?: string;
}

/**
 * Created voice profile
 * Stored at: nav_users/{uid}/created_voices/{voiceId}
 */
export interface CreatedVoiceProfile {
  id: string;
  userId: string;
  name: string;
  description: string;
  gender?: string;
  age?: string;
  status: CreatedVoiceStatus | string;
  isPaid: boolean;
  isPublic: boolean;
  isActive: boolean;
  qualityScore?: number;
  voiceEmbeddingPath?: string;
  previewAudioUrl?: string;
  downloadCount?: number;
  marketplaceStatus?: string;
  marketplaceLastPurchasedAt?: Timestamp | string;
  marketplaceCreditsEarnedCents?: number;
  createdAt?: Timestamp | string;
  updatedAt?: Timestamp | string;
  finalizedAt?: Timestamp | string;
  creatorDisplayName?: string;
  marketplaceSourceVoiceId?: string;
  userDisplayName?: string;
  userEmail?: string;
}

/**
 * Media-cloned voice profile
 * Stored at: nav_users/{uid}/media_cloned_voices/{voiceId}
 */
export interface MediaClonedVoiceProfile {
  id: string;
  userId: string;
  name: string;
  sourceType?: string;
  sourceUrl?: string;
  uploadedFileName?: string;
  status: string;
  isPaid: boolean;
  isActive: boolean;
  qualityScore?: number;
  usableSpeechSeconds?: number;
  previewAudioUrl?: string;
  voiceEmbeddingPath?: string;
  finalizedByAdmin?: boolean;
  failureReason?: string;
  failureMessage?: string;
  createdAt?: Timestamp | string;
  updatedAt?: Timestamp | string;
  finalizedAt?: Timestamp | string;
  userDisplayName?: string;
  userEmail?: string;
}

export interface RecordingEntry {
  phrase: string;
  url: string;
  duration: number;
  recordedAt: Timestamp | string;
}

/**
 * Voice cloning statistics
 */
export interface VoiceStats {
  totalVoices: number;
  byStatus: Record<VoiceRecordingStatus, number>;
  byRelationship: Record<string, number>;
  paidVoices: number;
  unpaidVoices: number;
  failedCount: number;
  processingCount: number;
  readyCount: number;
  avgQualityScore: number;
  totalCachedPhrases: number;
}

/**
 * Filter options for voice listing
 */
export interface VoiceFilterOptions {
  status?: VoiceRecordingStatus;
  relationship?: string;
  isPaid?: boolean;
  isActive?: boolean;
  userId?: string;
  searchQuery?: string;
  limitCount?: number;
}

/**
 * Filter options for created voice listing
 */
export interface CreatedVoiceFilterOptions {
  status?: CreatedVoiceStatus | string;
  isPaid?: boolean;
  isPublic?: boolean;
  userId?: string;
  searchQuery?: string;
  limitCount?: number;
}

export interface MediaClonedVoiceFilterOptions {
  status?: string;
  isPaid?: boolean;
  sourceType?: string;
  userId?: string;
  searchQuery?: string;
  limitCount?: number;
}

function getTimestampValue(value?: Timestamp | string): number {
  if (!value) return 0;
  if (value instanceof Timestamp) return value.toMillis();
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

// ============================================================================
// VOICE CLONE RETRIEVAL
// ============================================================================

/**
 * Get all voice clones across all users with optional filters
 */
export async function getVoiceClones(options: VoiceFilterOptions = {}): Promise<VoiceClone[]> {
  const { limitCount = 200 } = options;
  
  // Get all users first (to attach user info to voices)
  const usersSnap = await getDocs(collection(navDb, NAV_USERS_COLLECTION));
  const usersMap = new Map<string, { displayName: string; email: string }>();
  
  usersSnap.docs.forEach(doc => {
    if (!['blocked', 'invites', 'admin_logs'].includes(doc.id)) {
      const data = doc.data();
      usersMap.set(doc.id, {
        displayName: data.displayName || data.firstName || data.email || 'Unknown',
        email: data.email || ''
      });
    }
  });

  // Collect all voices from all users
  const voices: VoiceClone[] = [];
  
  for (const [userId, userInfo] of usersMap) {
    try {
      const voicesRef = collection(navDb, NAV_USERS_COLLECTION, userId, VOICES_SUBCOLLECTION);
      const voicesSnap = await getDocs(voicesRef);
      
      voicesSnap.docs.forEach(doc => {
        const data = doc.data();
        const voice: VoiceClone = {
          id: doc.id,
          userId,
          name: data.name || '',
          relationship: data.relationship || 'OTHER',
          nickname: data.nickname || '',
          phone: data.phone,
          email: data.email,
          wakeWord: data.wakeWord,
          volume: data.volume ?? 1.0,
          pitch: data.pitch ?? 1.0,
          speed: data.speed ?? 1.0,
          isActive: data.isActive ?? false,
          recordingStatus: data.recordingStatus || VOICE_RECORDING_STATUS.PENDING,
          recordingToken: data.recordingToken,
          recordingSessionId: data.recordingSessionId,
          isPaid: data.isPaid ?? false,
          purchaseToken: data.purchaseToken,
          voiceId: data.voiceId,
          elevenLabsVoiceId: data.elevenLabsVoiceId,
          qualityScore: data.qualityScore,
          rawRecordingUrl: data.rawRecordingUrl,
          processedRecordingUrl: data.processedRecordingUrl,
          cachedPhrases: data.cachedPhrases,
          createdAt: data.createdAt,
          recordedAt: data.recordedAt,
          processedAt: data.processedAt,
          updatedAt: data.updatedAt,
          lastError: data.lastError,
          errorCount: data.errorCount,
          lastRetryAt: data.lastRetryAt,
          userDisplayName: userInfo.displayName,
          userEmail: userInfo.email
        };
        voices.push(voice);
      });
    } catch (error) {
      // Skip users with permission issues
      console.warn(`Failed to fetch voices for user ${userId}:`, error);
    }
  }

  // Apply filters
  let filteredVoices = voices;

  if (options.status) {
    filteredVoices = filteredVoices.filter(v => v.recordingStatus === options.status);
  }

  if (options.relationship) {
    filteredVoices = filteredVoices.filter(v => v.relationship === options.relationship);
  }

  if (options.isPaid !== undefined) {
    filteredVoices = filteredVoices.filter(v => v.isPaid === options.isPaid);
  }

  if (options.isActive !== undefined) {
    filteredVoices = filteredVoices.filter(v => v.isActive === options.isActive);
  }

  if (options.userId) {
    filteredVoices = filteredVoices.filter(v => v.userId === options.userId);
  }

  if (options.searchQuery) {
    const query = options.searchQuery.toLowerCase();
    filteredVoices = filteredVoices.filter(v => 
      v.name.toLowerCase().includes(query) ||
      v.nickname.toLowerCase().includes(query) ||
      v.userDisplayName?.toLowerCase().includes(query) ||
      v.userEmail?.toLowerCase().includes(query) ||
      v.userId.toLowerCase().includes(query)
    );
  }

  // Sort by createdAt descending (newest first)
  filteredVoices.sort((a, b) => {
    const aDate = a.createdAt instanceof Timestamp ? a.createdAt.toMillis() : new Date(a.createdAt).getTime();
    const bDate = b.createdAt instanceof Timestamp ? b.createdAt.toMillis() : new Date(b.createdAt).getTime();
    return bDate - aDate;
  });

  // Apply limit
  return filteredVoices.slice(0, limitCount);
}

/**
 * Get a specific voice clone by ID
 */
export async function getVoiceById(userId: string, voiceId: string): Promise<VoiceClone | null> {
  const voiceRef = doc(navDb, NAV_USERS_COLLECTION, userId, VOICES_SUBCOLLECTION, voiceId);
  const voiceSnap = await getDoc(voiceRef);
  
  if (!voiceSnap.exists()) {
    return null;
  }

  // Get user info
  const userRef = doc(navDb, NAV_USERS_COLLECTION, userId);
  const userSnap = await getDoc(userRef);
  const userData = userSnap.exists() ? userSnap.data() : {};

  const data = voiceSnap.data();
  return {
    id: voiceSnap.id,
    userId,
    name: data.name || '',
    relationship: data.relationship || 'OTHER',
    nickname: data.nickname || '',
    phone: data.phone,
    email: data.email,
    wakeWord: data.wakeWord,
    volume: data.volume ?? 1.0,
    pitch: data.pitch ?? 1.0,
    speed: data.speed ?? 1.0,
    isActive: data.isActive ?? false,
    recordingStatus: data.recordingStatus || VOICE_RECORDING_STATUS.PENDING,
    recordingToken: data.recordingToken,
    recordingSessionId: data.recordingSessionId,
    isPaid: data.isPaid ?? false,
    purchaseToken: data.purchaseToken,
    voiceId: data.voiceId,
    elevenLabsVoiceId: data.elevenLabsVoiceId,
    qualityScore: data.qualityScore,
    rawRecordingUrl: data.rawRecordingUrl,
    processedRecordingUrl: data.processedRecordingUrl,
    cachedPhrases: data.cachedPhrases,
    createdAt: data.createdAt,
    recordedAt: data.recordedAt,
    processedAt: data.processedAt,
    updatedAt: data.updatedAt,
    lastError: data.lastError,
    errorCount: data.errorCount,
    lastRetryAt: data.lastRetryAt,
    userDisplayName: userData.displayName || userData.firstName || userData.email || 'Unknown',
    userEmail: userData.email || ''
  };
}

/**
 * Get voice clones for a specific user
 */
export async function getUserVoices(userId: string): Promise<VoiceClone[]> {
  return getVoiceClones({ userId });
}

function mapCreatedVoiceDoc(
  docSnapshot: any,
  userId: string,
  userData?: { displayName: string; email: string }
): CreatedVoiceProfile {
  const data = docSnapshot.data();
  return {
    id: docSnapshot.id,
    userId,
    name: data.name || '',
    description: data.description || '',
    gender: data.gender,
    age: data.age,
    status: data.status || CREATED_VOICE_STATUS.DESIGNING,
    isPaid: data.isPaid ?? false,
    isPublic: data.isPublic ?? false,
    isActive: data.isActive ?? false,
    qualityScore: data.qualityScore,
    voiceEmbeddingPath: data.voiceEmbeddingPath,
    previewAudioUrl: data.previewAudioUrl,
    downloadCount: data.downloadCount ?? 0,
    marketplaceStatus: data.marketplaceStatus,
    marketplaceLastPurchasedAt: data.marketplaceLastPurchasedAt,
    marketplaceCreditsEarnedCents: data.marketplaceCreditsEarnedCents ?? 0,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
    finalizedAt: data.finalizedAt,
    creatorDisplayName: data.creatorDisplayName,
    marketplaceSourceVoiceId: data.marketplaceSourceVoiceId,
    userDisplayName: userData?.displayName || data.creatorDisplayName || 'Unknown',
    userEmail: userData?.email || ''
  };
}

function mapMediaClonedVoiceDoc(
  docSnapshot: any,
  userId: string,
  userData?: { displayName: string; email: string }
): MediaClonedVoiceProfile {
  const data = docSnapshot.data();
  return {
    id: docSnapshot.id,
    userId,
    name: data.name || '',
    sourceType: data.sourceType,
    sourceUrl: data.sourceUrl,
    uploadedFileName: data.uploadedFileName,
    status: data.status || 'DRAFT',
    isPaid: data.isPaid ?? false,
    isActive: data.isActive ?? false,
    qualityScore: data.qualityScore,
    usableSpeechSeconds: data.usableSpeechSeconds,
    previewAudioUrl: data.previewAudioUrl,
    voiceEmbeddingPath: data.voiceEmbeddingPath,
    finalizedByAdmin: data.finalizedByAdmin,
    failureReason: data.failureReason,
    failureMessage: data.failureMessage,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
    finalizedAt: data.finalizedAt,
    userDisplayName: userData?.displayName || 'Unknown',
    userEmail: userData?.email || ''
  };
}

/**
 * Get created voices across all users with optional filters.
 */
export async function getCreatedVoices(
  options: CreatedVoiceFilterOptions = {}
): Promise<CreatedVoiceProfile[]> {
  const { limitCount = 200 } = options;

  const usersSnap = await getDocs(collection(navDb, NAV_USERS_COLLECTION));
  const usersMap = new Map<string, { displayName: string; email: string }>();

  usersSnap.docs.forEach((userDoc) => {
    if (!['blocked', 'invites', 'admin_logs'].includes(userDoc.id)) {
      const data = userDoc.data();
      usersMap.set(userDoc.id, {
        displayName: data.displayName || data.firstName || data.email || 'Unknown',
        email: data.email || ''
      });
    }
  });

  const voices: CreatedVoiceProfile[] = [];

  if (options.userId) {
    const createdVoicesRef = collection(navDb, NAV_USERS_COLLECTION, options.userId, CREATED_VOICES_SUBCOLLECTION);
    const snapshot = await getDocs(createdVoicesRef);
    const userData = usersMap.get(options.userId);

    snapshot.docs.forEach((voiceDoc) => {
      voices.push(mapCreatedVoiceDoc(voiceDoc, options.userId!, userData));
    });
  } else {
    const snapshot = await getDocs(collectionGroup(navDb, CREATED_VOICES_SUBCOLLECTION));

    snapshot.docs.forEach((voiceDoc) => {
      const userId = voiceDoc.ref.parent.parent?.id;
      if (!userId) return;
      voices.push(mapCreatedVoiceDoc(voiceDoc, userId, usersMap.get(userId)));
    });
  }

  let filteredVoices = voices;

  if (options.status) {
    filteredVoices = filteredVoices.filter((voice) => voice.status === options.status);
  }

  if (options.isPaid !== undefined) {
    filteredVoices = filteredVoices.filter((voice) => voice.isPaid === options.isPaid);
  }

  if (options.isPublic !== undefined) {
    filteredVoices = filteredVoices.filter((voice) => voice.isPublic === options.isPublic);
  }

  if (options.searchQuery) {
    const queryText = options.searchQuery.toLowerCase();
    filteredVoices = filteredVoices.filter((voice) =>
      voice.name.toLowerCase().includes(queryText) ||
      voice.description.toLowerCase().includes(queryText) ||
      voice.id.toLowerCase().includes(queryText) ||
      voice.userId.toLowerCase().includes(queryText) ||
      (voice.userDisplayName || '').toLowerCase().includes(queryText) ||
      (voice.userEmail || '').toLowerCase().includes(queryText)
    );
  }

  return filteredVoices
    .sort((a, b) => getTimestampValue(b.updatedAt || b.createdAt) - getTimestampValue(a.updatedAt || a.createdAt))
    .slice(0, limitCount);
}

/**
 * Get created voices for a specific user.
 */
export async function getUserCreatedVoices(userId: string): Promise<CreatedVoiceProfile[]> {
  return getCreatedVoices({ userId, limitCount: 500 });
}

/**
 * Get media-cloned voices across all users with optional filters.
 */
export async function getMediaClonedVoices(
  options: MediaClonedVoiceFilterOptions = {}
): Promise<MediaClonedVoiceProfile[]> {
  const { limitCount = 200 } = options;

  const usersSnap = await getDocs(collection(navDb, NAV_USERS_COLLECTION));
  const usersMap = new Map<string, { displayName: string; email: string }>();

  usersSnap.docs.forEach((userDoc) => {
    if (!['blocked', 'invites', 'admin_logs'].includes(userDoc.id)) {
      const data = userDoc.data();
      usersMap.set(userDoc.id, {
        displayName: data.displayName || data.firstName || data.email || 'Unknown',
        email: data.email || ''
      });
    }
  });

  const voices: MediaClonedVoiceProfile[] = [];

  if (options.userId) {
    const mediaVoicesRef = collection(navDb, NAV_USERS_COLLECTION, options.userId, MEDIA_CLONED_VOICES_SUBCOLLECTION);
    const snapshot = await getDocs(mediaVoicesRef);
    const userData = usersMap.get(options.userId);

    snapshot.docs.forEach((voiceDoc) => {
      voices.push(mapMediaClonedVoiceDoc(voiceDoc, options.userId!, userData));
    });
  } else {
    const snapshot = await getDocs(collectionGroup(navDb, MEDIA_CLONED_VOICES_SUBCOLLECTION));

    snapshot.docs.forEach((voiceDoc) => {
      const userId = voiceDoc.ref.parent.parent?.id;
      if (!userId) return;
      voices.push(mapMediaClonedVoiceDoc(voiceDoc, userId, usersMap.get(userId)));
    });
  }

  let filteredVoices = voices;

  if (options.status) {
    filteredVoices = filteredVoices.filter((voice) => voice.status === options.status);
  }

  if (options.isPaid !== undefined) {
    filteredVoices = filteredVoices.filter((voice) => voice.isPaid === options.isPaid);
  }

  if (options.sourceType) {
    filteredVoices = filteredVoices.filter((voice) => voice.sourceType === options.sourceType);
  }

  if (options.searchQuery) {
    const queryText = options.searchQuery.toLowerCase();
    filteredVoices = filteredVoices.filter((voice) =>
      voice.name.toLowerCase().includes(queryText) ||
      (voice.sourceUrl || '').toLowerCase().includes(queryText) ||
      (voice.uploadedFileName || '').toLowerCase().includes(queryText) ||
      voice.id.toLowerCase().includes(queryText) ||
      voice.userId.toLowerCase().includes(queryText) ||
      (voice.userDisplayName || '').toLowerCase().includes(queryText) ||
      (voice.userEmail || '').toLowerCase().includes(queryText)
    );
  }

  return filteredVoices
    .sort((a, b) => getTimestampValue(b.updatedAt || b.createdAt) - getTimestampValue(a.updatedAt || a.createdAt))
    .slice(0, limitCount);
}

/**
 * Get media-cloned voices for a specific user.
 */
export async function getUserMediaClonedVoices(userId: string): Promise<MediaClonedVoiceProfile[]> {
  return getMediaClonedVoices({ userId, limitCount: 500 });
}

/**
 * Get recording sessions for a voice
 */
export async function getVoiceRecordingSessions(
  userId: string, 
  voiceId: string
): Promise<VoiceRecordingSession[]> {
  const sessionsRef = collection(navDb, NAV_USERS_COLLECTION, userId, RECORDING_SESSIONS_SUBCOLLECTION);
  const q = query(sessionsRef, where('voiceId', '==', voiceId), orderBy('startedAt', 'desc'));
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as VoiceRecordingSession[];
}

// ============================================================================
// VOICE STATISTICS
// ============================================================================

/**
 * Get voice cloning statistics
 */
export async function getVoiceStats(): Promise<VoiceStats> {
  const voices = await getVoiceClones({ limitCount: 10000 });
  
  const stats: VoiceStats = {
    totalVoices: voices.length,
    byStatus: {
      [VOICE_RECORDING_STATUS.PENDING]: 0,
      [VOICE_RECORDING_STATUS.RECORDING]: 0,
      [VOICE_RECORDING_STATUS.PROCESSING]: 0,
      [VOICE_RECORDING_STATUS.READY]: 0,
      [VOICE_RECORDING_STATUS.FAILED]: 0
    },
    byRelationship: {},
    paidVoices: 0,
    unpaidVoices: 0,
    failedCount: 0,
    processingCount: 0,
    readyCount: 0,
    avgQualityScore: 0,
    totalCachedPhrases: 0
  };

  let qualityScoreSum = 0;
  let qualityScoreCount = 0;

  voices.forEach(voice => {
    // By status
    const status = voice.recordingStatus || VOICE_RECORDING_STATUS.PENDING;
    if (stats.byStatus[status as VoiceRecordingStatus] !== undefined) {
      stats.byStatus[status as VoiceRecordingStatus]++;
    }

    // By relationship
    const rel = voice.relationship || 'OTHER';
    stats.byRelationship[rel] = (stats.byRelationship[rel] || 0) + 1;

    // Paid/unpaid
    if (voice.isPaid) {
      stats.paidVoices++;
    } else {
      stats.unpaidVoices++;
    }

    // Quick counts
    if (status === VOICE_RECORDING_STATUS.FAILED) stats.failedCount++;
    if (status === VOICE_RECORDING_STATUS.PROCESSING) stats.processingCount++;
    if (status === VOICE_RECORDING_STATUS.READY) stats.readyCount++;

    // Quality score
    if (voice.qualityScore !== undefined && voice.qualityScore > 0) {
      qualityScoreSum += voice.qualityScore;
      qualityScoreCount++;
    }

    // Cached phrases
    if (voice.cachedPhrases) {
      stats.totalCachedPhrases += Object.keys(voice.cachedPhrases).length;
    }
  });

  stats.avgQualityScore = qualityScoreCount > 0 ? qualityScoreSum / qualityScoreCount : 0;

  return stats;
}

// ============================================================================
// VOICE STATUS MANAGEMENT
// ============================================================================

/**
 * Update voice recording status
 */
export async function updateVoiceStatus(
  userId: string,
  voiceId: string,
  status: VoiceRecordingStatus,
  adminId: string,
  notes?: string
): Promise<void> {
  const voiceRef = doc(navDb, NAV_USERS_COLLECTION, userId, VOICES_SUBCOLLECTION, voiceId);
  
  const updates: Record<string, any> = {
    recordingStatus: status,
    updatedAt: Timestamp.now()
  };

  // Clear error if marking as ready or processing
  if (status === VOICE_RECORDING_STATUS.READY || status === VOICE_RECORDING_STATUS.PROCESSING) {
    updates.lastError = null;
  }

  await updateDoc(voiceRef, updates);

  await logAdminAction(adminId, 'UPDATE_USER', `${userId}/voice/${voiceId}`, {
    action: 'update_voice_status',
    newStatus: status,
    notes
  });
}

/**
 * Toggle voice active status
 */
export async function toggleVoiceActive(
  userId: string,
  voiceId: string,
  isActive: boolean,
  adminId: string
): Promise<void> {
  const voiceRef = doc(navDb, NAV_USERS_COLLECTION, userId, VOICES_SUBCOLLECTION, voiceId);
  
  await updateDoc(voiceRef, {
    isActive,
    updatedAt: Timestamp.now()
  });

  await logAdminAction(adminId, 'UPDATE_USER', `${userId}/voice/${voiceId}`, {
    action: isActive ? 'enable_voice' : 'disable_voice'
  });
}

/**
 * Retry failed voice processing via Cloud Function
 */
export async function retryVoiceProcessing(
  userId: string,
  voiceId: string,
  adminId: string
): Promise<{ success: boolean; message: string }> {
  try {
    // First update status to PROCESSING
    const voiceRef = doc(navDb, NAV_USERS_COLLECTION, userId, VOICES_SUBCOLLECTION, voiceId);
    await updateDoc(voiceRef, {
      recordingStatus: VOICE_RECORDING_STATUS.PROCESSING,
      lastRetryAt: Timestamp.now(),
      lastError: null,
      updatedAt: Timestamp.now()
    });

    // Call the Cloud Function to reprocess
    const processVoice = httpsCallable(functions, 'retryVoiceProcessing');
    const result = await processVoice({ userId, voiceId, adminTriggered: true });
    const data = result.data as { success: boolean; message?: string };

    await logAdminAction(adminId, 'UPDATE_USER', `${userId}/voice/${voiceId}`, {
      action: 'retry_voice_processing',
      success: data.success,
      message: data.message
    });

    return {
      success: data.success,
      message: data.message || (data.success ? 'Processing started' : 'Processing failed')
    };
  } catch (error: any) {
    // Update status back to FAILED
    const voiceRef = doc(navDb, NAV_USERS_COLLECTION, userId, VOICES_SUBCOLLECTION, voiceId);
    await updateDoc(voiceRef, {
      recordingStatus: VOICE_RECORDING_STATUS.FAILED,
      lastError: error.message || 'Retry failed',
      errorCount: (await getDoc(voiceRef)).data()?.errorCount || 0 + 1,
      updatedAt: Timestamp.now()
    });

    await logAdminAction(adminId, 'UPDATE_USER', `${userId}/voice/${voiceId}`, {
      action: 'retry_voice_processing',
      success: false,
      error: error.message
    });

    return {
      success: false,
      message: error.message || 'Failed to retry processing'
    };
  }
}

/**
 * Regenerate recording token for a voice
 */
export async function regenerateRecordingToken(
  userId: string,
  voiceId: string,
  adminId: string
): Promise<string> {
  const newToken = `${userId}_${voiceId}_${Date.now()}_${Math.random().toString(36).substring(7)}`;
  
  const voiceRef = doc(navDb, NAV_USERS_COLLECTION, userId, VOICES_SUBCOLLECTION, voiceId);
  await updateDoc(voiceRef, {
    recordingToken: newToken,
    recordingStatus: VOICE_RECORDING_STATUS.PENDING,
    updatedAt: Timestamp.now()
  });

  await logAdminAction(adminId, 'UPDATE_USER', `${userId}/voice/${voiceId}`, {
    action: 'regenerate_recording_token'
  });

  return newToken;
}

// ============================================================================
// VOICE DELETION
// ============================================================================

/**
 * Delete a voice clone with full cleanup
 * This calls the Cloud Function which handles:
 * - Voice synthesis data cleanup
 * - Firebase Storage cleanup
 * - Firestore document deletion
 */
export async function deleteVoice(
  userId: string,
  voiceId: string,
  adminId: string,
  reason?: string
): Promise<{ success: boolean; message: string }> {
  try {
    // Get voice data first for audit
    const voice = await getVoiceById(userId, voiceId);
    
    // Call the Cloud Function for cleanup
    const deleteVoiceFn = httpsCallable(functions, 'deleteLovedOneVoice');
    const result = await deleteVoiceFn({ userId, voiceId, adminTriggered: true });
    const data = result.data as { success: boolean; message?: string };

    await logAdminAction(adminId, 'DELETE_USER', `${userId}/voice/${voiceId}`, {
      action: 'delete_voice',
      voiceName: voice?.name,
      relationship: voice?.relationship,
      reason,
      success: data.success
    });

    return {
      success: data.success,
      message: data.message || (data.success ? 'Voice deleted successfully' : 'Deletion failed')
    };
  } catch (error: any) {
    // Fallback: try direct Firestore deletion if Cloud Function fails
    try {
      const voiceRef = doc(navDb, NAV_USERS_COLLECTION, userId, VOICES_SUBCOLLECTION, voiceId);
      await deleteDoc(voiceRef);

      await logAdminAction(adminId, 'DELETE_USER', `${userId}/voice/${voiceId}`, {
        action: 'delete_voice',
        reason,
        success: true,
        note: 'Direct deletion (Cloud Function unavailable)'
      });

      return {
        success: true,
        message: 'Voice deleted (manual cleanup may be required for Storage)'
      };
    } catch (deleteError: any) {
      return {
        success: false,
        message: deleteError.message || 'Failed to delete voice'
      };
    }
  }
}

/**
 * Delete a created voice profile with listing and marker cleanup.
 */
export async function deleteCreatedVoice(
  userId: string,
  voiceId: string,
  adminId: string,
  reason?: string
): Promise<{ success: boolean; message: string }> {
  const voiceRef = doc(navDb, NAV_USERS_COLLECTION, userId, CREATED_VOICES_SUBCOLLECTION, voiceId);

  try {
    const voiceDoc = await getDoc(voiceRef);
    const voiceData = voiceDoc.exists() ? mapCreatedVoiceDoc(voiceDoc, userId) : undefined;

    const deleteCreatedVoiceFn = httpsCallable(functions, 'deleteCreatedVoice');
    const result = await deleteCreatedVoiceFn({ userId, voiceId, adminTriggered: true });
    const data = result.data as { success: boolean; message?: string };

    await logAdminAction(adminId, 'DELETE_USER', `${userId}/created_voice/${voiceId}`, {
      action: 'delete_created_voice',
      voiceName: voiceData?.name,
      isPaid: voiceData?.isPaid,
      isPublic: voiceData?.isPublic,
      reason,
      success: data.success
    });

    return {
      success: data.success,
      message: data.message || (data.success ? 'Created voice deleted successfully' : 'Deletion failed')
    };
  } catch (error: any) {
    await logAdminAction(adminId, 'DELETE_USER', `${userId}/created_voice/${voiceId}`, {
      action: 'delete_created_voice',
      reason,
      success: false,
      error: error.message || String(error)
    });

    return {
      success: false,
      message: error.message || 'Failed to delete created voice'
    };
  }
}

export async function unpublishCreatedVoice(
  userId: string,
  voiceId: string,
  adminId: string,
  reason?: string
): Promise<{ success: boolean; message: string }> {
  try {
    const unpublishCreatedVoiceFn = httpsCallable(functions, 'unpublishPublicCreatedVoice');
    const result = await unpublishCreatedVoiceFn({ userId, voiceId });
    const data = result.data as { success: boolean; message?: string };

    await logAdminAction(adminId, 'UPDATE_USER', `${userId}/created_voice/${voiceId}`, {
      action: 'unpublish_created_voice',
      reason,
      success: data.success
    });

    return {
      success: data.success,
      message: data.message || (data.success ? 'Created voice unpublished successfully' : 'Unpublish failed')
    };
  } catch (error: any) {
    await logAdminAction(adminId, 'UPDATE_USER', `${userId}/created_voice/${voiceId}`, {
      action: 'unpublish_created_voice',
      reason,
      success: false,
      error: error.message || String(error)
    });

    return {
      success: false,
      message: error.message || 'Failed to unpublish created voice'
    };
  }
}

/**
 * Delete a media-cloned voice profile with private asset cleanup.
 */
export async function deleteMediaClonedVoice(
  userId: string,
  voiceId: string,
  adminId: string,
  reason?: string
): Promise<{ success: boolean; message: string }> {
  const voiceRef = doc(navDb, NAV_USERS_COLLECTION, userId, MEDIA_CLONED_VOICES_SUBCOLLECTION, voiceId);

  try {
    const voiceDoc = await getDoc(voiceRef);
    const voiceData = voiceDoc.exists() ? mapMediaClonedVoiceDoc(voiceDoc, userId) : undefined;

    const deleteMediaCloneFn = httpsCallable(functions, 'deleteMediaClonedVoice');
    const result = await deleteMediaCloneFn({ userId, voiceId, adminTriggered: true });
    const data = result.data as { success: boolean; message?: string };

    await logAdminAction(adminId, 'DELETE_USER', `${userId}/media_cloned_voice/${voiceId}`, {
      action: 'delete_media_cloned_voice',
      voiceName: voiceData?.name,
      sourceType: voiceData?.sourceType,
      isPaid: voiceData?.isPaid,
      reason,
      success: data.success
    });

    return {
      success: data.success,
      message: data.message || (data.success ? 'Media-cloned voice deleted successfully' : 'Deletion failed')
    };
  } catch (error: any) {
    try {
      await deleteDoc(voiceRef);

      await logAdminAction(adminId, 'DELETE_USER', `${userId}/media_cloned_voice/${voiceId}`, {
        action: 'delete_media_cloned_voice',
        reason,
        success: true,
        note: 'Direct deletion fallback used; storage cleanup may still be required'
      });

      return {
        success: true,
        message: 'Media-cloned voice deleted (storage cleanup may still be required)'
      };
    } catch (deleteError: any) {
      await logAdminAction(adminId, 'DELETE_USER', `${userId}/media_cloned_voice/${voiceId}`, {
        action: 'delete_media_cloned_voice',
        reason,
        success: false,
        error: deleteError.message || String(deleteError)
      });

      return {
        success: false,
        message: deleteError.message || 'Failed to delete media-cloned voice'
      };
    }
  }
}

// ============================================================================
// VOICE QUALITY MANAGEMENT
// ============================================================================

/**
 * Update quality score manually
 */
export async function updateQualityScore(
  userId: string,
  voiceId: string,
  qualityScore: number,
  adminId: string
): Promise<void> {
  if (qualityScore < 0 || qualityScore > 1) {
    throw new Error('Quality score must be between 0 and 1');
  }

  const voiceRef = doc(navDb, NAV_USERS_COLLECTION, userId, VOICES_SUBCOLLECTION, voiceId);
  await updateDoc(voiceRef, {
    qualityScore,
    updatedAt: Timestamp.now()
  });

  await logAdminAction(adminId, 'UPDATE_USER', `${userId}/voice/${voiceId}`, {
    action: 'update_quality_score',
    qualityScore
  });
}

/**
 * Clear cached phrases for a voice
 */
export async function clearCachedPhrases(
  userId: string,
  voiceId: string,
  adminId: string
): Promise<void> {
  const voiceRef = doc(navDb, NAV_USERS_COLLECTION, userId, VOICES_SUBCOLLECTION, voiceId);
  await updateDoc(voiceRef, {
    cachedPhrases: {},
    updatedAt: Timestamp.now()
  });

  await logAdminAction(adminId, 'UPDATE_USER', `${userId}/voice/${voiceId}`, {
    action: 'clear_cached_phrases'
  });
}

// ============================================================================
// PAYMENT MANAGEMENT
// ============================================================================

/**
 * Mark voice as paid (admin override)
 */
export async function markVoiceAsPaid(
  userId: string,
  voiceId: string,
  adminId: string,
  reason?: string
): Promise<void> {
  const voiceRef = doc(navDb, NAV_USERS_COLLECTION, userId, VOICES_SUBCOLLECTION, voiceId);
  await updateDoc(voiceRef, {
    isPaid: true,
    purchaseToken: `admin_granted_${adminId}_${Date.now()}`,
    updatedAt: Timestamp.now()
  });

  await logAdminAction(adminId, 'GRANT_SUBSCRIPTION', `${userId}/voice/${voiceId}`, {
    action: 'mark_voice_paid',
    reason
  });
}

/**
 * Revoke voice payment (admin action)
 */
export async function revokeVoicePayment(
  userId: string,
  voiceId: string,
  adminId: string,
  reason?: string
): Promise<void> {
  const voiceRef = doc(navDb, NAV_USERS_COLLECTION, userId, VOICES_SUBCOLLECTION, voiceId);
  await updateDoc(voiceRef, {
    isPaid: false,
    isActive: false,
    purchaseToken: null,
    updatedAt: Timestamp.now()
  });

  await logAdminAction(adminId, 'CANCEL_SUBSCRIPTION', `${userId}/voice/${voiceId}`, {
    action: 'revoke_voice_payment',
    reason
  });
}

// ============================================================================
// BULK OPERATIONS
// ============================================================================

/**
 * Get all failed voices for batch retry
 */
export async function getFailedVoices(): Promise<VoiceClone[]> {
  return getVoiceClones({ status: VOICE_RECORDING_STATUS.FAILED });
}

/**
 * Bulk retry all failed voices
 */
export async function bulkRetryFailedVoices(
  adminId: string
): Promise<{ total: number; succeeded: number; failed: number }> {
  const failedVoices = await getFailedVoices();
  let succeeded = 0;
  let failed = 0;

  for (const voice of failedVoices) {
    try {
      const result = await retryVoiceProcessing(voice.userId, voice.id, adminId);
      if (result.success) {
        succeeded++;
      } else {
        failed++;
      }
    } catch {
      failed++;
    }
  }

  await logAdminAction(adminId, 'BULK_ACTION', 'voice_clones', {
    action: 'bulk_retry_failed',
    total: failedVoices.length,
    succeeded,
    failed
  });

  return { total: failedVoices.length, succeeded, failed };
}

// ============================================================================
// EXPORT
// ============================================================================

const navigatorXVoiceService = {
  // Constants
  VOICE_RECORDING_STATUS,
  VOICE_RELATIONSHIPS,
  CREATED_VOICE_STATUS,

  // Retrieval
  getVoiceClones,
  getVoiceById,
  getUserVoices,
  getCreatedVoices,
  getUserCreatedVoices,
  getVoiceRecordingSessions,

  // Statistics
  getVoiceStats,

  // Status Management
  updateVoiceStatus,
  toggleVoiceActive,
  retryVoiceProcessing,
  regenerateRecordingToken,

  // Deletion
  deleteVoice,
  deleteCreatedVoice,
  unpublishCreatedVoice,

  // Quality
  updateQualityScore,
  clearCachedPhrases,

  // Payment
  markVoiceAsPaid,
  revokeVoicePayment,

  // Bulk Operations
  getFailedVoices,
  bulkRetryFailedVoices
};

export default navigatorXVoiceService;
